import { ApolloClient, ApolloError, useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import { SearchCampaignsDocument } from 'generated-graphql/hooks/apollo-hooks';
import {
  SearchableCampaignSortableFields,
  SearchableSortDirection,
  SearchCampaignsQuery,
  SearchCampaignsQueryVariables
} from 'generated-graphql/graphql';
import { CAMPAIGNS_PAGE_SIZE } from '../utils/getDefaultCampaignsQueryVariables';

// OpenSearch `from + limit` deep-paging window; a safety bound on the loop.
const MAX_CAMPAIGNS = 10000;

type SearchCampaignsData = {
  searchCampaigns: NonNullable<SearchCampaignsQuery['searchCampaigns']>;
};

// Paging with `from` needs a total order, otherwise campaigns that share the
// primary sort value (e.g. an identical createdAt from a bulk import) can shift
// between requests and be dropped or duplicated at a page boundary. Append a
// unique-field tiebreaker so the ordering is deterministic across pages.
export const withIdTiebreaker = (
  sort: SearchCampaignsQueryVariables['sort']
) => {
  const entries = Array.isArray(sort) ? sort : sort ? [sort] : [];
  const hasId = entries.some(
    (entry) => entry?.field === SearchableCampaignSortableFields.Id
  );

  if (hasId) {
    return entries;
  }

  return [
    ...entries,
    {
      field: SearchableCampaignSortableFields.Id,
      direction: SearchableSortDirection.Asc
    }
  ];
};

const fetchAllSearchCampaigns = async (
  client: ApolloClient<object>,
  variables: SearchCampaignsQueryVariables
): Promise<SearchCampaignsData> => {
  const items: SearchCampaignsData['searchCampaigns']['items'] = [];
  const sort = withIdTiebreaker(variables.sort);

  while (items.length < MAX_CAMPAIGNS) {
    const { data } = await client.query<
      SearchCampaignsQuery,
      SearchCampaignsQueryVariables
    >({
      query: SearchCampaignsDocument,
      variables: {
        ...variables,
        sort,
        limit: CAMPAIGNS_PAGE_SIZE,
        from: items.length
      },
      fetchPolicy: 'network-only'
    });

    const page = data?.searchCampaigns?.items ?? [];
    items.push(...page);

    if (page.length < CAMPAIGNS_PAGE_SIZE) {
      break;
    }
  }

  return { searchCampaigns: { items } };
};

type UseAllSearchCampaignsParams = {
  variables: SearchCampaignsQueryVariables;
  skip: boolean;
  onCompleted: (data: SearchCampaignsData) => void;
};

export const useAllSearchCampaigns = ({
  variables,
  skip,
  onCompleted
}: UseAllSearchCampaignsParams) => {
  const client = useApolloClient();
  const [loading, setLoading] = useState(!skip);
  const [error, setError] = useState<ApolloError | undefined>(undefined);

  // Always call the latest onCompleted, even if the component re-renders mid
  // pagination and passes a new inline callback.
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const variablesKey = JSON.stringify(variables);

  const refetch = useCallback(async (): Promise<{
    data: SearchCampaignsData;
  }> => {
    setLoading(true);
    setError(undefined);
    try {
      const data = await fetchAllSearchCampaigns(client, variables);
      return { data };
    } catch (err) {
      // Surface the error to every caller (initial load and manual refetch)
      // via `error` state, then re-throw to preserve the Apollo refetch
      // contract for callers that await the result.
      setError(err as ApolloError);
      throw err;
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, variablesKey]);

  useEffect(() => {
    if (skip) {
      setLoading(false);
      return;
    }

    const request = { active: true };

    const load = async () => {
      try {
        const { data } = await refetch();
        if (request.active) {
          onCompletedRef.current(data);
        }
      } catch {
        // Error is already surfaced via `error` state in refetch.
      }
    };

    load();

    return () => {
      request.active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skip, variablesKey]);

  return { loading, error, refetch };
};
